import { Logger } from "../core/Logger.js";

/**
 * Defines the values for standard keys
 */
export const Key = {
  A: 'KeyA',
  B: 'KeyB',
  C: 'KeyC',
  D: 'KeyD',
  E: 'KeyE',
  F: 'KeyF',
  G: 'KeyG',
  H: 'KeyH',
  I: 'KeyI',
  J: 'KeyJ',
  K: 'KeyK',
  L: 'KeyL',
  M: 'KeyM',
  N: 'KeyN',
  O: 'KeyO',
  P: 'KeyP',
  Q: 'KeyQ',
  R: 'KeyR',
  S: 'KeyS',
  T: 'KeyT',
  U: 'KeyU',
  V: 'KeyV',
  W: 'KeyW',
  X: 'KeyX',
  Y: 'KeyY',
  Z: 'KeyZ',
  Digit0: 'Digit0',
  Digit1: 'Digit1',
  Digit2: 'Digit2',
  Digit3: 'Digit3',
  Digit4: 'Digit4',
  Digit5: 'Digit5',
  Digit6: 'Digit6',
  Digit7: 'Digit7',
  Digit8: 'Digit8',
  Digit9: 'Digit9',
  Enter: 'Enter',
  Escape: 'Escape',
  Backspace: 'Backspace',
  Tab: 'Tab',
  Space: 'Space',
  Minus: 'Minus',
  Equal: 'Equal',
  BracketLeft: 'BracketLeft',
  BracketRight: 'BracketRight',
  Backslash: 'Backslash',
  Semicolon: 'Semicolon',
  Quote: 'Quote',
  Backquote: 'Backquote',
  Comma: 'Comma',
  Period: 'Period',
  Slash: 'Slash',
  CapsLock: 'CapsLock',
  F1: 'F1',
  F2: 'F2',
  F3: 'F3',
  F4: 'F4',
  F5: 'F5',
  F6: 'F6',
  F7: 'F7',
  F8: 'F8',
  F9: 'F9',
  F10: 'F10',
  F11: 'F11',
  F12: 'F12',
  PrintScreen: 'PrintScreen',
  ScrollLock: 'ScrollLock',
  Pause: 'Pause',
  Insert: 'Insert',
  Home: 'Home',
  PageUp: 'PageUp',
  Delete: 'Delete',
  End: 'End',
  PageDown: 'PageDown',
  ArrowRight: 'ArrowRight',
  ArrowLeft: 'ArrowLeft',
  ArrowDown: 'ArrowDown',
  ArrowUp: 'ArrowUp',
  NumLock: 'NumLock',
  Numpad0: 'Numpad0',
  Numpad1: 'Numpad1',
  Numpad2: 'Numpad2',
  Numpad3: 'Numpad3',
  Numpad4: 'Numpad4',
  Numpad5: 'Numpad5',
  Numpad6: 'Numpad6',
  Numpad7: 'Numpad7',
  Numpad8: 'Numpad8',
  Numpad9: 'Numpad9',
  NumpadAdd: 'NumpadAdd',
  NumpadSubtract: 'NumpadSubtract',
  NumpadMultiply: 'NumpadMultiply',
  NumpadDivide: 'NumpadDivide',
  NumpadDecimal: 'NumpadDecimal',
  NumpadEnter: 'NumpadEnter',
  ShiftLeft: 'ShiftLeft',
  ShiftRight: 'ShiftRight',
  ControlLeft: 'ControlLeft',
  ControlRight: 'ControlRight',
  AltLeft: 'AltLeft',
  AltRight: 'AltRight'
};

/**
 * Provides a static interface for capturing and managing keyboard input events.
 * It allows you to detect when keys are pressed, track currently pressed keys,
} * and check if a key has been pressed for a specified duration.
 */
export class KeyboardInput {
  constructor() {
    this._keys = new Set();
    this._keyPressTimes = new Map();
    this._keyReleaseTimes = new Map();
    this._keyUpNotified = new Map();
    this._keyPressIntervals = new Map();
    this._frameSnapshot = null;
  }

  /**
   * Starts capturing keyboard input.   
   */
  start() {
    if (typeof window === 'undefined') {
      Logger.warn('Window is not defined. Event listeners cannot be registered.');
      return;
    }

    this.keydownHandler = event => this.handleKeyDown(event);
    this.keyupHandler = event => this.handleKeyUp(event);

    window.addEventListener('keydown', this.keydownHandler);
    window.addEventListener('keyup', this.keyupHandler);
  }

  // Handle keydown event
  handleKeyDown(event) {
    const keyCode = event.code;
    if (!this._keys.has(keyCode)) {
      this._keys.add(keyCode);
      this._keyPressTimes.set(keyCode, Date.now());
      this._keyReleaseTimes.delete(keyCode);
      this._keyUpNotified.delete(keyCode);
      this._keyPressIntervals.set(keyCode, Date.now());
    }
  }

  // Handle keyup event
  handleKeyUp(event) {
    const keyCode = event.code;
    if (this._keys.has(keyCode)) {
      this._keys.delete(keyCode);
      this._keyPressTimes.delete(keyCode);
      this._keyReleaseTimes.set(keyCode, Date.now());
      this._keyUpNotified.set(keyCode, false);
      this._keyPressIntervals.delete(keyCode);
    }
  }

  // private method to ensure frame snapshot is created
  _ensureSnapshot() {
    if (this._frameSnapshot) return;

    const snapshot = {
      keys: new Set(this._keys),
      keyPressIntervals: new Map(this._keyPressIntervals),
      keyReleaseFlags: new Map()
    };

    for (const [key, wasNotified] of this._keyUpNotified.entries()) {
      const isReleased = !this._keys.has(key) && this._keyReleaseTimes.has(key);
      if (isReleased && !wasNotified) {
        snapshot.keyReleaseFlags.set(key, true);
      }
    }

    this._frameSnapshot = snapshot;
  }

  /**
   * Returns an array of currently pressed key codes.
   */
  pressedKeys() {
    this._ensureSnapshot();
    return Array.from(this._frameSnapshot.keys);
  }

  /**
  * Returns true only once if the key was previously pressed and then released.
  * @param {string} key The key code to check.
  */
  isKeyReleased(key) {
    this._ensureSnapshot();
    const released = this._frameSnapshot.keyReleaseFlags.get(key);
    if (released) {
      this._keyUpNotified.set(key, true);
      return true;
    }
    return false;
  }

  /**
   * Checks if a specific key is currently pressed.
   * @param {string} key The key code to check.
   */
  isKeyPressed(key) {
    this._ensureSnapshot();
    return this._frameSnapshot.keys.has(key);
  }

  /**
   * Checks if a specific key has been pressed for a specified duration
   * @param {string} key The key code to check.
   * @param {number} durationInSeconds The duration in seconds.
   */
  isKeyPressedFor(key, durationInSeconds) {
    this._ensureSnapshot();
    if (!this._frameSnapshot.keys.has(key)) return false;

    const start = this._frameSnapshot.keyPressIntervals.get(key);
    if (!start) return false;

    const elapsed = (Date.now() - start) / 1000;
    if (elapsed >= durationInSeconds) {
      this._keyPressIntervals.set(key, Date.now());
      return true;
    }

    return false;
  }

  /*
   * Call this at the end of runtime frame to reset the frame snapshot.
   */
  eventFrameEnd() {
    this._frameSnapshot = null;
  }

  /**
   * Stops capturing keyboard input.
   */
  stop() {
    this._keys.clear();
    this._keyPressTimes.clear();
    this._keyReleaseTimes.clear();
    this._keyUpNotified.clear();
    this._keyPressIntervals.clear();
    this._frameSnapshot = null;

    window.removeEventListener('keydown', this.keydownHandler);
    window.removeEventListener('keyup', this.keyupHandler);
  }
}